import React, {useMemo, useState} from "react";
import {Badge, Box, Button, Stack, SxProps, Theme, Tooltip, Typography} from "@mui/material";
import {ChevronLeft, ChevronRight} from "@mui/icons-material";
import {DateTime, Settings} from "luxon";
import {DateCalendar, LocalizationProvider, PickersDay, PickersDayProps} from "@mui/x-date-pickers";
import {AdapterLuxon} from "@mui/x-date-pickers/AdapterLuxon";
import {PregnancyListJson} from "../../../api/generated/medical-rest";
import {JsonAnimal} from "../../../api/generated/rest-dto";
import {AnimalService} from "../../../services/animal-service";

Settings.defaultWeekSettings = {
    firstDay: 1, // Monday
    minimalDays: 4,
    weekend: [6, 7],
};

enum PregnancyEventType {
    Start = "start",
    End = "end",
}

interface DateEvents {
    type: PregnancyEventType;
    pregnancies: PregnancyInfo[];
}

interface PregnancyInfo {
    pregnancy: PregnancyListJson;
    damName: string;
    type: PregnancyEventType;
}

const buildDateKey = (year: number, month: number, day: number) =>
    `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

function buildEventMap(
    pregnancies: PregnancyListJson[],
    animalsById: Map<string, JsonAnimal>
): Map<string, DateEvents> {
    const map = new Map<string, DateEvents>();

    function addEvent(date: DateTime, type: PregnancyEventType, p: PregnancyListJson) {
        const dam = animalsById.get(p.damId);
        const damName = AnimalService.getAnimalFullName(dam);
        const key = buildDateKey(date.year, date.month, date.day);
        const info: PregnancyInfo = {pregnancy: p, damName, type};
        const existing = map.get(key);
        if (existing) {
            existing.pregnancies.push(info);
        } else {
            map.set(key, {type, pregnancies: [info]});
        }
    }

    for (const p of pregnancies) {
        if (p.startDate) {
            addEvent(p.startDate, PregnancyEventType.Start, p);
        }
        const endDate = p.actualEndDate ?? p.calculatedEndDate;
        if (endDate) {
            addEvent(endDate, PregnancyEventType.End, p);
        }
    }

    return map;
}

const formatEndLabel = (p: PregnancyListJson) =>
    p.actualEndDate ? "Geburt" : "Errechneter Geburtstermin";

function formatPregnancyStartDetail(p: PregnancyListJson): string {
    // Starting pregnancy: show (calculated) birthdate
    const endDate = p.actualEndDate ?? p.calculatedEndDate;
    if (!endDate) {
        return "";
    }
    const endStr = endDate.toLocaleString(DateTime.DATE_SHORT);
    return `${formatEndLabel(p)}: ${endStr}`;
}

function formatPregnancyEndDetail(p: PregnancyListJson): string {
    // Ending pregnancy: show start date only (end date is the hovered date)
    if (!p.startDate) {
        return "";
    }
    const startStr = p.startDate.toLocaleString(DateTime.DATE_SHORT);
    return `Trächtig seit: ${startStr}`;
}

const getBadgeColor = (type: PregnancyEventType | undefined) => {
    // Determine badge color: success (end) takes precedence over primary (start)
    switch (type) {
        case PregnancyEventType.End: return "success";
        case PregnancyEventType.Start: return "primary";
        default: return undefined;
    }
}

const PregnancyDay: React.FC<PickersDayProps & { events?: Map<string, DateEvents>; onPregnancyClick?: (pregnancyId: string) => void }> = (props) => {
    const {events, onPregnancyClick, day, outsideCurrentMonth, ...dayProps} = props;

    if (outsideCurrentMonth) {
        return <PickersDay {...dayProps} day={day} outsideCurrentMonth />;
    }

    const key = buildDateKey(day.year, day.month, day.day);
    const ev = events?.get(key) ?? null;

    const badgeColor = getBadgeColor(ev?.type);

    const dayContent = (
        <Badge
            color={badgeColor}
            variant="dot"
            invisible={!badgeColor}
            overlap="circular"
        >
            <PickersDay {...dayProps} day={day} outsideCurrentMonth={false} />
        </Badge>
    );

    if (!ev?.pregnancies.length) {
        return dayContent;
    }

    const tooltipContent = (
        <Stack spacing={1} sx={{maxWidth: 260}}>
            {ev.pregnancies.map((pi, i) => {
                const label = pi.type === PregnancyEventType.Start
                    ? "Trächtigkeitsbeginn"
                    : formatEndLabel(pi.pregnancy);
                const detail = pi.type === PregnancyEventType.Start
                    ? formatPregnancyStartDetail(pi.pregnancy)
                    : formatPregnancyEndDetail(pi.pregnancy);
                return (
                    <Box
                        key={i}
                        onClick={() => onPregnancyClick?.(pi.pregnancy.id)}
                        sx={{cursor: 'pointer'}}
                    >
                        <Typography variant="body2" sx={{fontWeight: "bold"}}>
                            {pi.damName}: {label}
                        </Typography>
                        {detail && <Typography variant="caption">{detail}</Typography>}
                    </Box>
                );
            })}
        </Stack>
    );

    return (
        <Tooltip title={tooltipContent} enterTouchDelay={0} leaveTouchDelay={0}>
            {dayContent}
        </Tooltip>
    );
};

const CalendarMonth: React.FC<{ referenceDate: DateTime; events: Map<string, DateEvents>; onPregnancyClick?: (pregnancyId: string) => void }> = ({referenceDate, events, onPregnancyClick}) => (
    <DateCalendar
        value={referenceDate}
        referenceDate={referenceDate}
        readOnly
        showDaysOutsideCurrentMonth={false}
        displayWeekNumber
        slots={{day: PregnancyDay}}
        slotProps={{
            day: () => ({
                events,
                onPregnancyClick,
                sx: {
                    '&.Mui-selected': {
                        bgcolor: 'transparent',
                        color: 'inherit',
                        '&:hover': {
                            bgcolor: (theme) => theme.palette.action.hover,
                        },
                    },
                },
            }),
            previousIconButton: { hidden: true },
            nextIconButton: { hidden: true },
        }}
    />
);

export interface PregnancyCalendarProps {
    pregnancies: PregnancyListJson[];
    animalsById: Map<string, JsonAnimal>;
    sx?: SxProps<Theme>;
    onPregnancyClick?: (pregnancyId: string) => void;
}

export const PregnancyCalendar: React.FC<PregnancyCalendarProps> = ({pregnancies, animalsById, sx, onPregnancyClick}) => {
    const now = DateTime.now();
    const [offset, setOffset] = useState(0);

    const events = useMemo(
        () => buildEventMap(pregnancies, animalsById),
        [pregnancies, animalsById]
    );

    const goPrev = () => setOffset(o => o - 1);
    const goNext = () => setOffset(o => o + 1);
    const goToday = () => setOffset(0);

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Stack direction="column" alignItems="center" sx={{width: "100%", ...sx}}>
                <Stack direction="row" justifyContent="center" spacing={1}>
                    <Button size="small" startIcon={<ChevronLeft />} onClick={goPrev}>
                        Zurück
                    </Button>
                    <Button size="small" onClick={goToday}>
                        Heute
                    </Button>
                    <Button size="small" endIcon={<ChevronRight />} onClick={goNext}>
                        Weiter
                    </Button>
                </Stack>

                <Stack direction={{xs: "column", sm: "row"}} spacing={3}>
                    <CalendarMonth referenceDate={now.plus({months: offset})} events={events} onPregnancyClick={onPregnancyClick} />
                    <CalendarMonth referenceDate={now.plus({months: offset + 1})} events={events} onPregnancyClick={onPregnancyClick} />
                </Stack>
            </Stack>
        </LocalizationProvider>
    );
};
